import {
  Badge,
  Checkbox,
  Group,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useLocation } from 'wouter';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { useUrlDraft, useUrlState } from '@/hooks/useUrlState';
import { formatDate } from '@/lib/helpers';
import { urlField } from '@/lib/urlState';
import {
  CLIENT_COMPANY_STATUSES,
  CLIENT_COMPANY_STATUS_COLORS,
  CLIENT_COMPANY_STATUS_LABELS,
  jurisdictionTitle,
  useClientCompaniesSearch,
  usePortfolioContacts,
} from '@/models/clientCompanies';
import {
  CLIENT_PORTFOLIO_FULL,
  useHasAnyPermission,
} from '@/models/permissions';

const SCHEMA = {
  tab: urlField.enum(['companies', 'contacts'] as const, 'companies'),
  search: urlField.string(),
  status: urlField.enum(['', ...CLIENT_COMPANY_STATUSES] as const, ''),
  unmapped: urlField.boolean(),
  page: urlField.page(),
  pageSize: urlField.number(50, { integer: true, min: 1, max: 200 }),
};

const STATUS_OPTIONS = CLIENT_COMPANY_STATUSES.map((value) => ({
  value,
  label: CLIENT_COMPANY_STATUS_LABELS[value],
}));

const UNMAPPED_BADGE_STYLES = {
  root: { flexShrink: 0, maxWidth: 'none', overflow: 'visible' },
  label: { overflow: 'visible', textOverflow: 'clip' },
};

const fullName = (first: string | null, last: string | null) =>
  [first, last].filter(Boolean).join(' ') || '-';

export default function ClientCompaniesPage() {
  const [state, setState, { clear, isFiltered }] = useUrlState(SCHEMA);
  const [searchInput, setSearchInput] = useUrlDraft(state.search, (search) =>
    setState({ search }),
  );
  const [, navigate] = useLocation();
  const fullAccess = useHasAnyPermission(CLIENT_PORTFOLIO_FULL);
  const tab = fullAccess ? state.tab : 'companies';
  const paging = {
    q: state.search || undefined,
    limit: state.pageSize,
    offset: (state.page - 1) * state.pageSize,
  };
  const companies = useClientCompaniesSearch(
    {
      ...paging,
      status: state.status || undefined,
      unmapped: state.unmapped,
    },
    tab === 'companies',
  );
  const contacts = usePortfolioContacts(paging, tab === 'contacts');
  const total =
    (tab === 'companies' ? companies.data?.total : contacts.data?.total) ?? 0;

  return (
    <Paper p="md" h="100%">
      <Stack gap="md">
        <Title order={2}>Client Companies</Title>

        <Tabs
          value={tab}
          onChange={(v) => v && setState({ tab: v as typeof state.tab })}
        >
          <Tabs.List>
            <Tabs.Tab value="companies">Companies</Tabs.Tab>
            {fullAccess && (
              <Tabs.Tab value="contacts">Contacts with portfolio</Tabs.Tab>
            )}
          </Tabs.List>
        </Tabs>

        <Group>
          <TextInput
            placeholder={
              tab === 'companies'
                ? 'Search name, reg. number, Monday ID'
                : 'Search name, email, Monday ID'
            }
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            w={320}
          />
          {tab === 'companies' && (
            <>
              <Select
                placeholder="Any status"
                data={STATUS_OPTIONS}
                value={state.status || null}
                onChange={(v) =>
                  setState({ status: (v ?? '') as typeof state.status })
                }
                clearable
                w={200}
              />
              <Checkbox
                label="Has unmapped services"
                checked={state.unmapped}
                onChange={(e) =>
                  setState({ unmapped: e.currentTarget.checked })
                }
              />
            </>
          )}
          <ClearFiltersButton visible={isFiltered} onClick={clear} />
        </Group>

        {tab === 'companies' ? (
          <Table
            highlightOnHover
            fz="xs"
            styles={{
              td: { whiteSpace: 'nowrap' },
              th: { whiteSpace: 'nowrap' },
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Company</Table.Th>
                <Table.Th>Reg. number</Table.Th>
                <Table.Th>Jurisdiction</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Account</Table.Th>
                <Table.Th>Services</Table.Th>
                <Table.Th>Next renewal</Table.Th>
                <Table.Th>Monday ID</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(companies.data?.items ?? []).map((r) => (
                <Table.Tr
                  key={r.id}
                  onClick={() => navigate(`~/client-companies/${r.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Text fz="xs" fw={500}>
                        {r.name}
                      </Text>
                      {r.isTest && (
                        <Badge size="xs" color="gray" variant="outline">
                          Test
                        </Badge>
                      )}
                    </Group>
                    {r.legalName && r.legalName !== r.name && (
                      <Text fz="xs" c="dimmed">
                        {r.legalName}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{r.registrationNumber ?? '-'}</Table.Td>
                  <Table.Td>{jurisdictionTitle(r.jurisdiction)}</Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      variant="light"
                      color={CLIENT_COMPANY_STATUS_COLORS[r.status]}
                    >
                      {CLIENT_COMPANY_STATUS_LABELS[r.status] ?? r.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{r.accountName ?? '-'}</Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <span>{r.services}</span>
                      {r.unmapped > 0 && (
                        <Badge
                          size="xs"
                          color="orange"
                          variant="light"
                          styles={UNMAPPED_BADGE_STYLES}
                        >
                          {r.unmapped} unmapped
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {r.nextRenewalDate ? formatDate(r.nextRenewalDate) : '-'}
                  </Table.Td>
                  <Table.Td>{r.mondayItemId ?? '-'}</Table.Td>
                </Table.Tr>
              ))}
              {!companies.isLoading &&
                (companies.data?.items ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={8} align="center">
                      No companies
                    </Table.Td>
                  </Table.Tr>
                )}
            </Table.Tbody>
          </Table>
        ) : (
          <Table
            highlightOnHover
            fz="xs"
            styles={{
              td: { whiteSpace: 'nowrap' },
              th: { whiteSpace: 'nowrap' },
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Contact</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Account</Table.Th>
                <Table.Th>Services</Table.Th>
                <Table.Th>Not delivered</Table.Th>
                <Table.Th>Monday ID</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(contacts.data?.items ?? []).map((r) => (
                <Table.Tr
                  key={r.id}
                  onClick={() => navigate(`~/client-contacts/${r.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td>{fullName(r.firstName, r.lastName)}</Table.Td>
                  <Table.Td>{r.email ?? '-'}</Table.Td>
                  <Table.Td>{r.accountName ?? '-'}</Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <span>{r.services}</span>
                      {r.unmapped > 0 && (
                        <Badge
                          size="xs"
                          color="orange"
                          variant="light"
                          styles={UNMAPPED_BADGE_STYLES}
                        >
                          {r.unmapped} unmapped
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>{r.pending}</Table.Td>
                  <Table.Td>{r.mondayItemId ?? '-'}</Table.Td>
                </Table.Tr>
              ))}
              {!contacts.isLoading &&
                (contacts.data?.items ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6} align="center">
                      No contacts
                    </Table.Td>
                  </Table.Tr>
                )}
            </Table.Tbody>
          </Table>
        )}

        <Group justify="space-between">
          <Text fz="xs" c="dimmed">
            {total} total
          </Text>
          <Pagination
            total={Math.max(1, Math.ceil(total / state.pageSize))}
            value={state.page}
            onChange={(page) => setState({ page })}
          />
        </Group>
      </Stack>
    </Paper>
  );
}
